import * as angular from 'angular';
import * as _ from 'lodash';
import ngRedux from 'ng-redux';

import { ComponentDefinition, ConnectionPointType, EmailEventDTO, Miniature, IApprovalStrategy, ContextualField, PropertyType, EmailEventType, TimeUnit, TicketStatus } from '../../../models';
import { allTimeUnits, mailingEventJobUpdated, guidEmpty } from '../../../common';
import { ILoggerService, IEventsService, IApprovalStrategyService, ICurrentApplicationService } from '../../../services';
import { MixedUserChoice } from '../../../models/mailing/MixedUserChoice';
import { toRecipients } from '../../../models/mailing/RecipientList';
import * as Fields from '../../../common/helpers/contextual';
import { ITicketStatusActionsService, Store } from '../../../stateManagement';
import { EventEmailDefinition } from '../../../models/DTO/EventEmailDefinition';
import { remapMailRecipients } from '../../../models/mailing/remapMailRecipients';

enum Property {
    Subject,
    ConnectionPoint,
    Event,
    DateFormat,
    IsSingleDashboardFile,
    Severity,
    Groups,
    GroupTypes,
    AlarmCategories,
    Snooze,
    ApprovalStrategy,
    ApprovalLevels,
    TicketStatuses,
    IsExportToChildApplication
}

class EditEventEmailContextualController implements angular.IComponentController {
    approvalStrategyLevels: any[];
    externalFormController: angular.IFormController;
    formController: angular.IFormController;
    isLoading: boolean;
    loadingMessage: string;
    miniatures: Miniature[];
    previousStrategy: string;
    properties: ContextualField[];
    redirect: boolean;
    selectedItem: EmailEventDTO;
    ticketStatusList: any[];
    timeUnits: any = allTimeUnits;
    types = [ConnectionPointType.SMTP];

    private _unsubscribe: Function;
    loadTicketStatusList: () => angular.IPromise<TicketStatus[]>;

    static $inject = [
        '$rootScope',
        '$scope',
        '$state',
        '$translate',
        '$ngRedux',
        'eventsService',
        'approvalStrategyService',
        'currentApplicationService',
        'loggerService',
        'ticketStatusActionsService'
    ];

    constructor(
        private _$rootScope: angular.IRootScopeService,
        private _$scope: angular.IScope,
        private _$state: angular.ui.IStateService,
        private _$translate: angular.translate.ITranslateService,
        private _$ngRedux: ngRedux.INgRedux,
        private _eventsService: IEventsService,
        private _approvalStrategyService: IApprovalStrategyService,
        private _currentApplicationService: ICurrentApplicationService,
        private _loggerService: ILoggerService,
        private _ticketStatusesActionService: ITicketStatusActionsService
    ) {
    }

    $onInit() {
        this.miniatures = [];

        this.initRedux();

        // Initialize ticket status list
        this.initialize();
        const connectionPointGuid = Object.assign({}, Fields.ConnectionPointGuid);
        connectionPointGuid.extraData = {
            types: this.types,
            subProperty: 'eventEmailDefinition'
        };

        const isSingleDashboardFile = angular.copy(Fields.IsSingleDashboardFile);
        isSingleDashboardFile.extraData = { subProperty: 'eventEmailDefinition' };

        this.properties = [
            {
                modelName: 'subject',
                propertyType: PropertyType.Text,
                required: true,
                icon: 'fa-header',
                label: 'Subject',
                propertyName: 'subjectProperty',
                messages: [{
                    key: 'required', value: 'A subject is required'
                }],
                extraData: {
                    subProperty: 'eventEmailDefinition'
                }
            },
            connectionPointGuid,
            {
                modelName: 'event',
                propertyType: PropertyType.TriggerEventSelector,
                required: true,
                icon: 'fa-bolt',
                label: 'Event',
                propertyName: 'eventProperty',
                messages: [{
                    key: 'required', value: 'An event is required'
                }]
            },
            angular.copy(Fields.DateFormat),
            isSingleDashboardFile,
            angular.copy(Fields.AlarmSeverity),
            angular.copy(Fields.GroupGuids),
            angular.copy(Fields.GroupTypes),
            angular.copy(Fields.AlarmCategories),
            {
                modelName: 'snooze',
                propertyType: PropertyType.Frequency,
                iconText: 'Zz',
                label: 'Snooze time',
                propertyName: 'snoozeProperty',
                extraData: {
                    intervalUnitName: 'unit',
                    disableFrequencyValidation: true
                }
            },
            this.getApprovalStrategyField(),
            this.getApprovalLevelsField(),
            this.getTicketStatusField(),
            angular.copy(Fields.IsExportToChildApplication)
        ];

        this.computeVisibility();
        this.previousStrategy = this.selectedItem.approvalStrategyGuid;

        this._$scope.$watch(() => this.selectedItem, () => {
            if (this.selectedItem && this.selectedItem.approvalStrategyGuid) {
                this.changeStrategy(this.selectedItem.approvalStrategyGuid);
            }
            this.computeVisibility();
        });

        this._$scope.$watch(() => this.selectedItem ? this.selectedItem.event : null, () => {
            this.computeVisibility();
        });
    }

    $onDestroy() {
        if (this._unsubscribe) {
            this._unsubscribe();
        }
    }

    // Setup redux to retrieve tickets and ticket statuses
    initRedux() {
        this._unsubscribe = this._$ngRedux
            .connect((store: Store) => {
                const { ticketStatuses } = store;

                return {
                    ticketStatusList: _.values(ticketStatuses.dictionary),
                    isLoading: ticketStatuses.isLoading,
                    isLoaded: ticketStatuses.isLoaded
                };
            }, dispatch => {
                return {
                    loadTicketStatusList: () => dispatch(this._ticketStatusesActionService.getAll())
                };
            })(this);
    }

    initialize(): void {
        this.isLoading = true;
        this.loadTicketStatusList()
            .then((data) => {
                if (!this.selectedItem.ticketStatuses) {
                    this.selectedItem.ticketStatuses = [];
                }
                this.ticketStatusList = data.map((ticketStatus: TicketStatus) => {
                    return { key: ticketStatus.id, value: ticketStatus.name };
                });
                this.properties[Property.TicketStatuses].extraData.options = this.ticketStatusList;
            })
            .finally(() => this.isLoading = false);
    }

    private getApprovalStrategyField(): ContextualField {
        const result = angular.copy(Fields.ApprovalStrategyGuid);
        result.onChange = (guid) => this.changeStrategy(guid);
        return result;
    }

    private getApprovalLevelsField(): ContextualField {
        const result = angular.copy(Fields.ApprovalLevels);
        result.extraData = {
            options: this.approvalStrategyLevels
        };
        return result;
    }

    private getTicketStatusField(): ContextualField {
        const result = angular.copy(Fields.TicketStatuses);
        result.extraData = {
            options: this.ticketStatusList
        };
        return result;
    }

    private computeVisibility() {
        const isAlarm = this.selectedItem
            && this.selectedItem.event === EmailEventType.AlarmRaised;
        const isApproval = this.selectedItem
            && (this.selectedItem.event === EmailEventType.Approval
                || this.selectedItem.event === EmailEventType.Rejection);
        const isTicketStatuses = this.selectedItem
            && (this.selectedItem.event === EmailEventType.TicketStatuses);
        const hasChild = this._currentApplicationService
            .getApplicationConfiguration().isChildApplicationCreationAllowed;

        // crappy hack to enable feeding snoozeInterval and snoozeTimeUnit properties
        // directly on object (and not in a subproperty)
        if (this.selectedItem) {
            const item = this.selectedItem;
            (item as any).snooze = {
                get interval(): number {
                    return item.snoozeInterval;
                },
                set interval(value: number) {
                    item.snoozeInterval = value;
                },
                get unit(): TimeUnit {
                    return item.snoozeTimeUnit;
                },
                set unit(value: TimeUnit) {
                    item.snoozeTimeUnit = value;
                }
            };
        }

        this.properties[Property.Subject].hide = false;
        this.properties[Property.ConnectionPoint].hide = false;
        this.properties[Property.Event].disabled = this.selectedItem?.guid !== guidEmpty;
        this.properties[Property.Event].hide = false;
        this.properties[Property.DateFormat].hide = false;
        this.properties[Property.Severity].hide = !isAlarm;
        this.properties[Property.Groups].hide = !isAlarm;
        this.properties[Property.GroupTypes].hide = !isAlarm;
        this.properties[Property.AlarmCategories].hide = !isAlarm;
        this.properties[Property.Snooze].hide = !isAlarm;
        this.properties[Property.ApprovalLevels].hide = !isApproval;
        this.properties[Property.ApprovalStrategy].hide = !isApproval;
        this.properties[Property.TicketStatuses].hide = !isTicketStatuses;
        this.properties[Property.IsExportToChildApplication].hide = !hasChild;
    }

    save() {
        this.selectedItem.eventEmailDefinition.dashboards = this.selectedItem.eventEmailDefinition.dashboards
            ? this.selectedItem.eventEmailDefinition.dashboards.filter(d => d.guid && d.guid !== guidEmpty)
            : [];

        // prevent duplicate
        const mail = this.selectedItem.eventEmailDefinition;
        remapMailRecipients(mail, mail.selectedChoices);
        return this._eventsService
            .addOrUpdateEventEmail(this.selectedItem)
            .then(data => {
                this.selectedItem.guid = data.guid;
                this.clean();
                this._$rootScope.$broadcast(mailingEventJobUpdated);
                this._loggerService.logSuccess(this._$translate.instant('Event based e-mail successfully saved.'));
            })
            .catch(() => {
                this._loggerService.logError(this._$translate.instant('Failed to save event based e-mail.'));
            });
    }

    private rebuildRecipients(mail: EventEmailDefinition) {
        const choices: MixedUserChoice[] = mail.selectedChoices;
        if (choices) {
            mail.recipients = toRecipients(choices);
        }
    }

    cancel() {
        this.clean();
    }

    isSaveEnabled() {
        let isValid = this.formController.$valid && (this.formController.$dirty);
        if (this.externalFormController) {
            isValid = isValid || (this.externalFormController.$dirty && this.externalFormController.$valid);
        }
        return isValid;
    }

    changeStrategy(guid: string) {
        this._approvalStrategyService
            .getAll()
            .then(all => {
                const strategy = _.find<IApprovalStrategy>(all, s => s.guid === guid);
                if (strategy) {
                    if (this.previousStrategy !== guid) {
                        this.selectedItem.approvalLevels = [];
                    }
                    this.approvalStrategyLevels = strategy
                        .approbationStrategyLevelCollection
                        .map(l => {
                            return { key: l.level, value: `L${(l.level + 1)}` };
                        });
                    this.properties[Property.ApprovalLevels].extraData.options = this.approvalStrategyLevels;
                }
            });
    }

    private clean() {
        this._$rootScope.$broadcast(mailingEventJobUpdated);
        this.formController.$setPristine();
        if (this.externalFormController) {
            this.externalFormController.$setPristine();
        }
        if (this.redirect) {
            this._$state.go('emails');
        }
        this.selectedItem = null;
    }
}

export const wtEditEventEmailContextual: ComponentDefinition = {
    componentId: 'wtEditEventEmailContextual',
    controller: EditEventEmailContextualController,
    template: require('./editEventEmailContextual.component.html'),
    controllerAs: 'vm',
    bindings: {
        selectedItem: '=',
        externalFormController: '=?',
        redirect: '<'
    }
};